import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Upload,
  Paperclip,
  FileText,
  Image,
  File,
  Download,
  Trash2,
  X,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Eye,
} from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPE_OPTIONS = [
  { value: 'evidence', label: 'Evidence' },
  { value: 'receipt', label: 'Mailing Receipt' },
  { value: 'delivery_confirmation', label: 'Delivery Confirmation' },
  { value: 'tracking_confirmation', label: 'Tracking Confirmation' },
  { value: 'affidavit', label: 'Affidavit' },
  { value: 'sworn_statement', label: 'Sworn Statement' },
  { value: 'response', label: 'Response Received' },
  { value: 'contract', label: 'Contract' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'court_document', label: 'Court Document' },
  { value: 'identification', label: 'Identification' },
  { value: 'photo', label: 'Photo / Screenshot' },
  { value: 'other', label: 'Other' },
];

const FILE_ICONS = {
  pdf: <FileText className="w-4 h-4 text-red-500" />,
  docx: <FileText className="w-4 h-4 text-blue-500" />,
  doc: <FileText className="w-4 h-4 text-blue-500" />,
  jpg: <Image className="w-4 h-4 text-green-500" />,
  jpeg: <Image className="w-4 h-4 text-green-500" />,
  png: <Image className="w-4 h-4 text-green-500" />,
  txt: <File className="w-4 h-4 text-gray-500" />,
};

export const EvidenceUploadModal = ({ conversationId, nodeId, nodeType, nodeLabel, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('evidence');
  const [description, setDescription] = useState('');
  const [relatedParty, setRelatedParty] = useState('');
  const [relatedClaim, setRelatedClaim] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (!file) return setError('Please select a file.');
    setUploading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('node_id', nodeId || '');
      formData.append('node_type', nodeType || '');
      formData.append('document_type', docType);
      formData.append('description', description);
      formData.append('related_party', relatedParty);
      formData.append('related_claim', relatedClaim);

      await axios.post(
        `${API_BASE}/api/strategy-brain/${conversationId}/evidence`,
        formData,
        { headers: { 'Authorization': `Bearer ${token}` }, timeout: 30000 }
      );
      onUploaded();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" data-testid="evidence-upload-modal">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Attach Evidence</CardTitle>
            {nodeLabel && <p className="text-xs text-gray-500 mt-0.5">Linking to: {nodeLabel}</p>}
          </div>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* File picker */}
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid="evidence-file-drop"
          >
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept=".pdf,.docx,.jpg,.jpeg,.png,.txt"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                {FILE_ICONS[file.name.split('.').pop()?.toLowerCase()] || <File className="w-4 h-4" />}
                <span className="text-sm font-medium text-gray-700">{file.name}</span>
                <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(0)}KB)</span>
              </div>
            ) : (
              <>
                <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                <p className="text-sm text-gray-500">Click to select file</p>
                <p className="text-xs text-gray-400">PDF, DOCX, JPG, PNG, TXT (max 10MB)</p>
              </>
            )}
          </div>

          {/* Document type */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Document Type</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full text-sm border rounded-md px-3 py-2 bg-white"
              data-testid="evidence-doc-type"
            >
              {DOC_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this evidence..."
              className="min-h-[60px] text-sm"
              data-testid="evidence-description"
            />
          </div>

          {/* Related party / claim */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Related Party</label>
              <Input
                value={relatedParty}
                onChange={(e) => setRelatedParty(e.target.value)}
                placeholder="e.g., ABC Corp"
                className="text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Related Claim</label>
              <Input
                value={relatedClaim}
                onChange={(e) => setRelatedClaim(e.target.value)}
                placeholder="e.g., Proof of claim"
                className="text-sm"
              />
            </div>
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <Button onClick={handleUpload} disabled={uploading || !file} className="w-full" data-testid="evidence-upload-btn">
            {uploading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
            ) : (
              <><Paperclip className="w-4 h-4 mr-2" />Attach Evidence</>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};


export const EvidenceList = ({ evidence, onDelete, onDownload }) => {
  if (!evidence || evidence.length === 0) {
    return (
      <div className="text-center py-3">
        <Paperclip className="w-5 h-5 mx-auto text-gray-300 mb-1" />
        <p className="text-xs text-gray-400">No evidence attached</p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="evidence-list">
      {evidence.map((ev) => (
        <div key={ev.evidence_id} className="flex items-center gap-2 bg-gray-50 rounded-md px-2.5 py-1.5 group">
          {FILE_ICONS[ev.file_type] || <File className="w-3.5 h-3.5 text-gray-400" />}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium text-gray-800 truncate">{ev.filename}</p>
            <div className="flex items-center gap-1.5">
              <Badge variant="outline" className="text-[9px] capitalize px-1 py-0">{(ev.document_type || '').replace(/_/g, ' ')}</Badge>
              {ev.verification_status === 'verified' && (
                <CheckCircle className="w-3 h-3 text-green-500" />
              )}
            </div>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onDownload(ev.evidence_id, ev.filename)}
              className="p-1 hover:bg-gray-200 rounded"
              title="Download"
              data-testid={`download-evidence-${ev.evidence_id}`}
            >
              <Download className="w-3 h-3 text-gray-500" />
            </button>
            <button
              onClick={() => onDelete(ev.evidence_id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete"
              data-testid={`delete-evidence-${ev.evidence_id}`}
            >
              <Trash2 className="w-3 h-3 text-red-400" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};


export const EvidenceBadge = ({ count, hasDeliveryProof, hasAffidavit, isMissing }) => {
  if (isMissing) {
    return (
      <div className="absolute -top-1.5 -right-1.5 z-10">
        <div className="bg-red-500 text-white text-[8px] font-bold rounded-full w-4 h-4 flex items-center justify-center" title="Evidence Needed">
          !
        </div>
      </div>
    );
  }
  if (count > 0) {
    return (
      <div className="absolute -top-1.5 -right-1.5 z-10">
        <div className={`${hasDeliveryProof || hasAffidavit ? 'bg-green-500' : 'bg-blue-500'} text-white text-[8px] font-bold rounded-full w-4 h-4 flex items-center justify-center`} title={`${count} attachment(s)`}>
          {count}
        </div>
      </div>
    );
  }
  return null;
};
